import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert, Spinner, Container, Row, Col, Button } from "react-bootstrap";
import { AuthContext } from "../contexts/AuthContext";
import api from "../api";

const GoogleCallback = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState({});
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const processingRef = useRef(false); // Prevent double execution
  const processedRef = useRef(false); // Track if we've already processed successfully

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution - check both processing and completed flags
      if (processingRef.current || processedRef.current) {
        console.log(
          "GoogleCallback: Already processing or completed, skipping..."
        );
        return;
      }

      // Get code from URL
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get("code");

      if (!code) {
        setError("Authentication failed: No authorization code received");
        setLoading(false);
        return;
      }

      // Mark as processing immediately
      processingRef.current = true;

      console.log("GoogleCallback: Processing code from Google OAuth...");

      // Log the code length for debugging (not the full code for security)
      console.log(`GoogleCallback: Code received (length: ${code.length})`);

      setDebugInfo((prev) => ({
        ...prev,
        codeLength: code.length,
        apiBaseUrl: api.defaults.baseURL,
      }));

      // Try the exchange-code endpoint (this is working correctly)
      try {
        console.log("GoogleCallback: Trying exchange-code endpoint...");
        const response = await api.post("/api/auth/google/exchange-code/", {
          code,
        });
        console.log("GoogleCallback: Exchange code successful");

        // Check for either 'access' or 'key' (backend fix pending)
        const accessToken = response.data.access || response.data.key;
        if (accessToken) {
          // Mark as successfully processed
          processedRef.current = true;

          // Login user with the tokens
          console.log(
            "GoogleCallback: Logging in user and redirecting to /data"
          );
          await login(accessToken, response.data.refresh);

          // Always redirect to data page
          navigate("/data", { replace: true });
          return;
        } else {
          setError("Authentication failed: No access token received");
          setLoading(false);
          processingRef.current = false;
          return;
        }
      } catch (exchangeError) {
        console.error("GoogleCallback: Exchange code failed:", exchangeError);

        // Log more detailed error information
        if (exchangeError.response) {
          console.error("Error response data:", exchangeError.response.data);
          console.error(
            "Error response status:",
            exchangeError.response.status
          );
        }

        setDebugInfo((prev) => ({
          ...prev,
          exchangeError: exchangeError.message,
          errorMessage: exchangeError.message,
          responseStatus: exchangeError.response?.status,
          responseData: JSON.stringify(exchangeError.response?.data || {}),
        }));

        setError(
          exchangeError.response?.data?.error ||
            exchangeError.response?.data?.detail ||
            "Authentication failed. Please try again."
        );
        setLoading(false);
        processingRef.current = false;
        return;
      }
    };

    handleCallback();

    // Don't reset processing flag in cleanup to prevent re-execution
  }, [login, navigate]); // Remove location.search to prevent re-runs

  const handleReturnToLogin = () => {
    navigate("/login");
  };

  if (loading) {
    return (
      <Container>
        <Row className="justify-content-center mt-5">
          <Col md={6} className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Completing authentication...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Row className="justify-content-center mt-5">
          <Col md={6}>
            <Alert variant="danger">
              <Alert.Heading>Authentication Error</Alert.Heading>
              <p>{error}</p>
              {error.includes("OAuth2Client") && (
                <p>
                  <strong>Technical details:</strong> There appears to be a
                  configuration issue with the OAuth provider. Please contact
                  the administrator.
                </p>
              )}
            </Alert>
            {Object.keys(debugInfo).length > 0 && (
              <Alert variant="info">
                <Alert.Heading>Debug Information</Alert.Heading>
                <pre style={{ fontSize: "0.8rem" }}>
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </Alert>
            )}
            <div className="text-center mt-3">
              <Button variant="primary" onClick={handleReturnToLogin}>
                Return to Login
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
};

export default GoogleCallback;
